/**
 * Seeded template `DefinitionState`s that testers can load into the builder
 * from the in-builder "Load template" menu.
 *
 * Each template is just a plain JSON object of the builder's state shape.
 * When the backend persistence layer lands these will be promoted to
 * server-side templates.
 */

import type { DefinitionState, DefinitionNode } from '~/pages/workflows/builder/types';

// Small helper to keep templates compact.
function n<T extends DefinitionNode>(node: T): T {
  return node;
}

const now = Date.now();

const cope: DefinitionState = {
  id: 'tpl_cope_verification',
  label: 'COPE Verification',
  description:
    'Read a property application, extract COPE (Construction, Occupancy, Protection, Exposure), verify against public records, and produce a report.',
  createdAt: now,
  updatedAt: now,
  inputs: [
    {
      key: 'slot_app',
      label: 'Application document',
      kind: 'FILE',
      required: true,
      enumerable: false,
      acceptedMimeTypes: ['application/pdf']
    }
  ],
  nodes: [
    n({
      id: 'n_input',
      taskKey: 'INPUT_DOCUMENT',
      inputSubtype: 'document',
      ownedSlotKey: 'slot_app',
      name: 'Application document',
      bindings: [],
      config: { ocrMode: 'auto' }
    }),
    n({
      id: 'n_extract',
      taskKey: 'EXTRACT',
      name: 'Extract COPE',
      bindings: [
        {
          inputKey: 'documents',
          source: { type: 'TASK_OUTPUT', sourceNodeId: 'n_input', sourceOutputKey: 'file' }
        }
      ],
      config: {
        citations: true,
        entities: [
          { kind: 'builtin', outputKey: 'cope', label: 'COPE' },
          { kind: 'builtin', outputKey: 'locations', label: 'Locations' }
        ]
      }
    }),
    n({
      id: 'n_verify',
      taskKey: 'VERIFY',
      name: 'Verify COPE against public records',
      bindings: [
        {
          inputKey: 'item',
          source: { type: 'TASK_OUTPUT', sourceNodeId: 'n_extract', sourceOutputKey: 'cope' }
        }
      ],
      forEach: {
        kind: 'TASK_OUTPUT',
        sourceNodeId: 'n_extract',
        sourceOutputKey: 'locations',
        inputKey: 'item'
      },
      config: {
        mode: 'web',
        criteria: ['construction', 'occupancy', 'protection', 'exposure'],
        reference_query: 'property inspection report'
      }
    }),
    n({
      id: 'n_aggregate',
      taskKey: 'AGGREGATE',
      name: 'Consolidate findings',
      bindings: [
        {
          inputKey: 'sources',
          source: { type: 'TASK_OUTPUT', sourceNodeId: 'n_verify', sourceOutputKey: 'result' }
        }
      ],
      config: { strategy: 'concat' }
    }),
    n({
      id: 'n_doc',
      taskKey: 'OUTPUT_DOCUMENT',
      name: 'COPE report',
      bindings: [
        {
          inputKey: 'data',
          source: { type: 'TASK_OUTPUT', sourceNodeId: 'n_aggregate', sourceOutputKey: 'result' }
        }
      ],
      config: {
        format: 'pdf',
        title: 'COPE Verification Report',
        prompt: 'Produce a one-page summary grouped by location.'
      }
    })
  ]
};

const sov: DefinitionState = {
  id: 'tpl_sov_builder',
  label: 'SOV Builder',
  description: 'Turn a list of properties into a polished Statement of Values spreadsheet.',
  createdAt: now,
  updatedAt: now,
  inputs: [
    {
      key: 'slot_rows',
      label: 'Property list',
      kind: 'FILE',
      required: true,
      enumerable: true,
      acceptedMimeTypes: [
        'text/csv',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ]
    }
  ],
  nodes: [
    n({
      id: 'n_input',
      taskKey: 'INPUT_SPREADSHEET',
      inputSubtype: 'spreadsheet',
      ownedSlotKey: 'slot_rows',
      name: 'Property list',
      bindings: [],
      config: { headerRow: 0, trimWhitespace: true }
    }),
    n({
      id: 'n_enrich',
      taskKey: 'VERIFY',
      name: 'Enrich each location',
      bindings: [
        {
          inputKey: 'item',
          source: { type: 'TASK_OUTPUT', sourceNodeId: 'n_input', sourceOutputKey: 'rows' }
        }
      ],
      forEach: {
        kind: 'TASK_OUTPUT',
        sourceNodeId: 'n_input',
        sourceOutputKey: 'rows',
        inputKey: 'item'
      },
      config: {
        mode: 'web',
        criteria: ['TIV', 'square footage', 'occupancy'],
        reference_query: '{address} property appraisal'
      }
    }),
    n({
      id: 'n_doc',
      taskKey: 'OUTPUT_DOCUMENT',
      name: 'SOV spreadsheet',
      bindings: [
        {
          inputKey: 'data',
          source: { type: 'TASK_OUTPUT', sourceNodeId: 'n_enrich', sourceOutputKey: 'result' }
        }
      ],
      config: {
        format: 'xlsx',
        title: 'Statement of Values',
        columns: [
          { header: 'Address', path: 'address' },
          { header: 'TIV', path: 'enriched.tiv' },
          { header: 'Square footage', path: 'enriched.sqft' },
          { header: 'Construction', path: 'enriched.construction' }
        ]
      }
    })
  ]
};

const accord: DefinitionState = {
  id: 'tpl_accord',
  label: 'Accord Form Generation',
  description: 'Pull account data from the AMS and render an ACORD 125 form.',
  createdAt: now,
  updatedAt: now,
  inputs: [
    {
      key: 'slot_account',
      label: 'Account',
      kind: 'AMS_SELECTION',
      required: true,
      enumerable: false
    }
  ],
  nodes: [
    n({
      id: 'n_input',
      taskKey: 'INPUT_AMS_READ',
      inputSubtype: 'ams',
      ownedSlotKey: 'slot_account',
      name: 'AMS account',
      bindings: [],
      config: { entities: ['account', 'policies', 'drivers', 'locations'] }
    }),
    n({
      id: 'n_doc',
      taskKey: 'OUTPUT_DOCUMENT',
      name: 'ACORD 125',
      bindings: [
        {
          inputKey: 'data',
          source: { type: 'TASK_OUTPUT', sourceNodeId: 'n_input', sourceOutputKey: 'account' }
        }
      ],
      config: {
        format: 'pdf',
        title: 'ACORD 125',
        prompt: 'Fill the ACORD 125 template with the account, locations, and driver fields.'
      }
    }),
    n({
      id: 'n_integration',
      taskKey: 'OUTPUT_INTEGRATION',
      name: 'Attach to account',
      bindings: [
        {
          inputKey: 'data',
          source: { type: 'TASK_OUTPUT', sourceNodeId: 'n_doc', sourceOutputKey: 'file' }
        }
      ],
      config: {
        integrationType: 'ams360',
        actions: [
          {
            capabilityKey: 'upload_document',
            label: 'Upload ACORD 125',
            config: { category: 'application' }
          }
        ]
      }
    })
  ]
};

const renewal: DefinitionState = {
  id: 'tpl_renewal',
  label: 'Renewal Comparison',
  description: 'Compare an expiring policy vs. a renewal quote and flag differences.',
  createdAt: now,
  updatedAt: now,
  inputs: [
    {
      key: 'slot_expiring',
      label: 'Expiring policy',
      kind: 'FILE',
      required: true,
      enumerable: false,
      acceptedMimeTypes: ['application/pdf']
    },
    {
      key: 'slot_renewal',
      label: 'Renewal quote',
      kind: 'FILE',
      required: true,
      enumerable: false,
      acceptedMimeTypes: ['application/pdf']
    }
  ],
  nodes: [
    n({
      id: 'n_input_expiring',
      taskKey: 'INPUT_DOCUMENT',
      inputSubtype: 'document',
      ownedSlotKey: 'slot_expiring',
      name: 'Expiring policy',
      bindings: [],
      config: { ocrMode: 'auto' }
    }),
    n({
      id: 'n_input_renewal',
      taskKey: 'INPUT_DOCUMENT',
      inputSubtype: 'document',
      ownedSlotKey: 'slot_renewal',
      name: 'Renewal quote',
      bindings: [],
      config: { ocrMode: 'auto' }
    }),
    n({
      id: 'n_extract_exp',
      taskKey: 'EXTRACT',
      name: 'Extract expiring',
      bindings: [
        {
          inputKey: 'documents',
          source: { type: 'TASK_OUTPUT', sourceNodeId: 'n_input_expiring', sourceOutputKey: 'file' }
        }
      ],
      config: {
        citations: true,
        entities: [
          { kind: 'builtin', outputKey: 'coverage', label: 'Coverage' },
          { kind: 'builtin', outputKey: 'limits', label: 'Limits' },
          { kind: 'builtin', outputKey: 'endorsements', label: 'Endorsements' }
        ]
      }
    }),
    n({
      id: 'n_extract_ren',
      taskKey: 'EXTRACT',
      name: 'Extract renewal',
      bindings: [
        {
          inputKey: 'documents',
          source: { type: 'TASK_OUTPUT', sourceNodeId: 'n_input_renewal', sourceOutputKey: 'file' }
        }
      ],
      config: {
        citations: true,
        entities: [
          { kind: 'builtin', outputKey: 'coverage', label: 'Coverage' },
          { kind: 'builtin', outputKey: 'limits', label: 'Limits' },
          { kind: 'builtin', outputKey: 'endorsements', label: 'Endorsements' }
        ]
      }
    }),
    n({
      id: 'n_compare',
      taskKey: 'COMPARE',
      name: 'Compare policies',
      bindings: [
        {
          inputKey: 'left',
          source: {
            type: 'TASK_OUTPUT',
            sourceNodeId: 'n_extract_exp',
            sourceOutputKey: 'coverage'
          }
        },
        {
          inputKey: 'right',
          source: {
            type: 'TASK_OUTPUT',
            sourceNodeId: 'n_extract_ren',
            sourceOutputKey: 'coverage'
          }
        }
      ],
      config: { joinType: 'full', joinKey: 'coverageType' }
    }),
    n({
      id: 'n_doc',
      taskKey: 'OUTPUT_DOCUMENT',
      name: 'Renewal comparison report',
      bindings: [
        {
          inputKey: 'data',
          source: { type: 'TASK_OUTPUT', sourceNodeId: 'n_compare', sourceOutputKey: 'diff' }
        }
      ],
      config: {
        format: 'pdf',
        title: 'Renewal Comparison',
        prompt: 'Highlight coverages removed, added, and with changed limits.'
      }
    })
  ]
};

const underwriting: DefinitionState = {
  id: 'tpl_underwriting',
  label: 'Underwriting Analysis',
  description:
    'Extract submission data, flag risk factors against guidelines, and produce an analysis memo.',
  createdAt: now,
  updatedAt: now,
  inputs: [
    {
      key: 'slot_submission',
      label: 'Submission packet',
      kind: 'FILE',
      required: true,
      enumerable: false,
      acceptedMimeTypes: ['application/pdf']
    }
  ],
  nodes: [
    n({
      id: 'n_input',
      taskKey: 'INPUT_DOCUMENT',
      inputSubtype: 'document',
      ownedSlotKey: 'slot_submission',
      name: 'Submission packet',
      bindings: [],
      config: { ocrMode: 'auto' }
    }),
    n({
      id: 'n_extract',
      taskKey: 'EXTRACT',
      name: 'Extract submission data',
      bindings: [
        {
          inputKey: 'documents',
          source: { type: 'TASK_OUTPUT', sourceNodeId: 'n_input', sourceOutputKey: 'file' }
        }
      ],
      config: {
        citations: true,
        entities: [
          { kind: 'builtin', outputKey: 'policy_metadata', label: 'Policy metadata' },
          { kind: 'builtin', outputKey: 'cope', label: 'COPE' },
          { kind: 'builtin', outputKey: 'coverage', label: 'Coverage' },
          {
            kind: 'custom',
            outputKey: 'loss_history',
            label: 'Loss history',
            prompt: 'List prior losses with date, amount, and cause.'
          }
        ]
      }
    }),
    n({
      id: 'n_verify',
      taskKey: 'VERIFY',
      name: 'Apply underwriting guidelines',
      bindings: [
        {
          inputKey: 'item',
          source: {
            type: 'TASK_OUTPUT',
            sourceNodeId: 'n_extract',
            sourceOutputKey: 'policy_metadata'
          }
        }
      ],
      config: {
        mode: 'auto',
        criteria: [
          'loss history within appetite',
          'COPE meets guidelines',
          'no ineligible occupancies'
        ]
      }
    }),
    n({
      id: 'n_doc',
      taskKey: 'OUTPUT_DOCUMENT',
      name: 'Underwriting memo',
      bindings: [
        {
          inputKey: 'data',
          source: { type: 'TASK_OUTPUT', sourceNodeId: 'n_verify', sourceOutputKey: 'result' }
        }
      ],
      config: {
        format: 'docx',
        title: 'Underwriting Analysis',
        prompt: 'Produce a 1-page memo: Summary · Risk factors · Recommendation.'
      }
    })
  ]
};

export const TEMPLATES = [cope, sov, accord, renewal, underwriting];
